import React, { FC, ReactNode, useState, useCallback } from "react";
import ReactPlayer from "react-player";
import classNames from "classnames/dedupe";
import { VideoPlayLargeIcon } from "../../../icons";
import { Button } from "../../ActionsSelectors/Button";
import { Column, Row } from "../../Fundamentals/Grid";
import { Image } from "../../Utility/MediaBuilder/Image/Image";
import { IImageData } from "../../Utility/MediaBuilder/Image/Image.interface";
import { useVideo } from "../../Utility/MediaBuilder/Video";
import ProductVideo from "../ProductVideo/ProductVideo";

const MEDIA_MOBILE_BREAKPOINT = 768;

export interface IVideoContentWidgetProps {
  /**
   * Video source URL
   */
  videoUrl?: string;

  /**
   * Set starting thumbnail image for video (video cover)
   */
  thumbnailData?: IImageData;
  /**
   * Widget title text
   */
  title?: string;

  /**
   * Set widget content (text)
   */
  children?: ReactNode;
}

const VideoContentWidget: FC<IVideoContentWidgetProps> = ({
  videoUrl: url,
  thumbnailData,
  title,
  children,
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [displayThumbnail, setDisplayThumbnail] = useState(true);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const [videoState, _player, handlePlayPause] = useVideo({
    url,
    playing: false,
    muted: true,
    loop: true,
  });

  const toggleFullscreenVideo = useCallback(() => {
    setIsFullscreen(!isFullscreen);
  }, [isFullscreen]);
  const closeFullscreenVideo = useCallback(() => setIsFullscreen(false), []);

  const handleContainerClick = () => {
    if (window.innerWidth > MEDIA_MOBILE_BREAKPOINT) {
      setDisplayThumbnail(false);
      handlePlayPause();
    } else {
      toggleFullscreenVideo();
    }
  };

  return (
    <>
      <Row>
        <Column xs={12} md={9} classes={{ root: "mb-7 mb-md-8" }}>
          <div className="h2-enriched mb-6">{title}</div>
          <p className="p-medium mb-0">{children}</p>
        </Column>
        <Column xs={12} classes={{ root: "mb-6" }}>
          <div
            className="ratio ratio-16x9 ratio-img"
            onClick={handleContainerClick}
            data-testid="content-video-play"
          >
            {displayThumbnail && thumbnailData ? (
              <Image {...thumbnailData} />
            ) : (
              <ReactPlayer width="100%" height="100%" {...videoState} />
            )}
            <Button
              variant="icon"
              classes={{
                root: classNames(
                  "position-absolute top-50 start-50 translate-middle btn-animated-fade",
                  {
                    "uds-fade-out": !(!videoState?.playing || displayThumbnail),
                  },
                ),
              }}
            >
              <VideoPlayLargeIcon data-testid="icon-video-play-large" />
            </Button>
          </div>
        </Column>
      </Row>

      <ProductVideo
        url={url}
        isOpen={isFullscreen}
        onClose={closeFullscreenVideo}
      />
    </>
  );
};

export default VideoContentWidget;
